import React, { useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as yup from 'yup';
import { toast } from 'react-hot-toast';
import ClockLoader from 'shared/components/ClockLoader';
import { getSessionProfile } from 'shared/utils/sessionProfileStore';

const ROLE_OPTIONS = ['USER', 'SERVICEADMIN', 'MANAGER', 'ADMIN', 'SYSTEMADMIN'];
const BASIC_ROLE_OPTIONS = ['USER', 'SERVICEADMIN'];
const STATUS_OPTIONS = [
  'ACTIVE',
  'DISABLED',
  'PENDING',
  'DELETED',
  'VERIFIED',
];

const validationSchema = yup.object({
  name: yup.string().trim().required('Name is required'),
  email: yup.string().trim().email('Invalid email').required('Email is required'),
  role: yup.string().required('Role is required'),
  status: yup.string().required('Status is required'),
});

const roleAsString = (role) =>
  typeof role === 'string' ? role.trim().toUpperCase() : role?.name?.trim?.().toUpperCase?.() ?? '';


const EditTenantUserForAdmin = ({
  open,
  onClose,
  row,
  registryTenantId,
  tenantNameDefault,
  updateTenantUser,
  onSuccess,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const sessionProfile = getSessionProfile();
  const currentUserRole = roleAsString(sessionProfile?.role);
  const visibleRoleOptions =
    currentUserRole === 'ADMIN' ? ROLE_OPTIONS : BASIC_ROLE_OPTIONS;

  const initialValues = useMemo(
    () =>
      row
        ? {
            name: row.name ?? '',
            email: row.email ?? '',
            phoneNumber: row.phoneNumber ?? '',
            tenantName: row.tenantName ?? tenantNameDefault ?? '',
            role: row.role ?? 'USER',
            status: row.status ?? 'ACTIVE',
            password: '',
          }
        : {
            name: '',
            email: '',
            phoneNumber: '',
            tenantName: tenantNameDefault ?? '',
            role: 'USER',
            status: 'ACTIVE',
            password: '',
          },
    [row, tenantNameDefault]
  );

  if (!open || !row) return null;

  const handleSubmit = async (values, { resetForm }) => {
    setSubmitting(true);
    try {
      const payload = {
        id: row.id,
        name: values.name.trim(),
        email: values.email.trim(),
        phoneNumber: values.phoneNumber?.trim() || null,
        role: roleAsString(values.role),
        status: values.status,
        tenantId: registryTenantId,
        tenantName: values.tenantName?.trim() || null,
      };
      if (values.password && values.password.trim() !== '') {
        payload.password = values.password;
      }
      await updateTenantUser(payload);
      toast.success('User updated');
      resetForm();
      onSuccess?.();
      onClose();
    } catch (error) {
      const err =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        'Failed to update user';
      toast.error(typeof err === 'string' ? err : 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center', fontWeight: 800, fontSize: '2rem' }}>
        Edit user
      </DialogTitle>
      <Formik
        key={row.id}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
        }) => (
          <Form>
            <DialogContent sx={{ position: 'relative' }}>
              {submitting && (
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    bgcolor: 'rgba(255,255,255,0.75)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1,
                  }}
                >
                  <ClockLoader size={40} />
                </Box>
              )}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                  gap: 2,
                }}
              >
                <TextField
                  fullWidth
                  margin="dense"
                  name="name"
                  label="Name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="email"
                  label="Email"
                  value={values.email}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.email && Boolean(errors.email)}
                  helperText={touched.email && errors.email}
                />
                <TextField
                  fullWidth
                  margin="dense"
                  name="phoneNumber"
                  label="Phone"
                  value={values.phoneNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                />
                {/* <TextField
                  fullWidth
                  margin="dense"
                  name="tenantName"
                  label="Tenant name"
                  value={values.tenantName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                /> */}
                <FormControl fullWidth margin="dense">
                  <InputLabel id="edit-tu-role">Role</InputLabel>
                  <Select
                    labelId="edit-tu-role"
                    name="role"
                    label="Role"
                    value={visibleRoleOptions.includes(values.role) ? values.role : ''}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {visibleRoleOptions.map((r) => (
                      <MenuItem key={r} value={r}>
                        {r}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl fullWidth margin="dense">
                  <InputLabel id="edit-tu-status">Status</InputLabel>
                  <Select
                    labelId="edit-tu-status"
                    name="status"
                    label="Status"
                    value={values.status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  margin="dense"
                  name="password"
                  label="New password (optional)"
                  type="password"
                  value={values.password}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  helperText="Leave empty to keep current password"
                  sx={{ gridColumn: { xs: 'auto', sm: '1 / -1' } }}
                />
              </Box>
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" variant="contained" disabled={submitting}>
                Save
              </Button>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditTenantUserForAdmin;
